import { useState, useEffect } from "react";
import {
  MotifProgressLoader,
  MotifPagination,
  MotifFilters,
  MotifFormField,
  MotifCheckbox,
  MotifAccordion,
  MotifAccordionContent,
  MotifAccordionTrigger,
  MotifButton,
  MotifSearch,
  MotifIcon,
  MotifBadge,
  MotifSelect,
  MotifOption,
  MotifErrorMessage,
} from "@ey-xd/motif-react";
import { useSelector, useDispatch } from "react-redux";
import {
  resetProductState,
  fetchProductListData,
} from "../../store/slices/CTProductListSlice";
import { RootState, AppDispatch } from "../../store";
import "./CTMarketPlace.scss";
import { fetchPortfolioListData } from "../../store/slices/CTPortfolioListSlice";
import { IconoirFilterList } from "@ey-xd/motif-icon";
import { useNavigate } from "react-router";
import { ApiStatus } from "../../utils/apiStatus.enum";
import productListEmptyImg from "../../assets/images/empty_product_list_default.svg";
import ProductCard from "../../components/ProductCard";
import { IconoirArrowLeft } from "@ey-xd/motif-icon";
import BreadcrumbHome from "../../components/BreadcrumbHome";
import SearchInput from "../../components/SearchInput/SearchInput";
import { fetchSectorIndustryListData } from "../../store/slices/CTSectorIndustryListSlice";
import { containsEmoji } from "../../utils/common";
import { EMOJI_ERROR_MESSAGE } from "../../Constant";
import ErrorOrderPage from "../../components/ErrorOrderPage/ErrorOrderPage";
import NoSearchResult from "../../components/NoSearchResult/NoSearchResult";
import {
  AI_SMART_SEARCH,
  AI_SMART_SEARCH_LINK_PRODUCT_CARD_DESC,
  AI_SMART_SEARCH_LINK_TITLE,
  AI_SMART_SEARCH_LINK_BANNER_DESC1,
  AI_SMART_SEARCH_LINK_BANNER_DESC,
} from "../../Constant";

const CTMarketplace = () => {
  const breadcrumbItems = [
    {
      label: "External Submission",
      value: "Home",
      elementKey: "submission",
      text: "Home",
      url: `/`,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
    {
      label: "External Submission",
      value: "Marketplace",
      elementKey: "submission",
      text: "Marketplace",
      disabled: true,
    },
  ];

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [searchFilter, setSearchFilter] = useState("");
  const [accordions, setAccordions] = useState<
    { title: string; checkboxes: string[] }[]
  >([]);
  const [accordionsDet, setAccordionDet] = useState<
    {
      accordion: string;
      id: number;
      checkboxes: {
        name: string;
        checked: boolean;
        id: number;
      }[];
    }[]
  >([]);
  const [selectAll, setSelectAll] = useState<number[]>([]);
  const [badge, setBadge] = useState(0);
  const [noResult, setNoResult] = useState<any>(false);
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [openAccordionState, setOpenAccordionState] = useState(false);
  const [openAccordionClosed, setOpenAccordionClosed] = useState<number[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [accordionsDetApplied, setAccordionsDetApplied] = useState<
    {
      accordion: string;
      id: number;
      checkboxes: {
        name: string;
        checked: boolean;
        id: number;
      }[];
    }[]
  >([]);

  const [searchValidationError, setSearchValidationError] =
    useState<string>("");
  const [hasUserTyped, setHasUserTyped] = useState(false);

  const portfolioData = useSelector(
    (state: RootState) => state.CTPortfolioListSlice.data,
  );

  const sectorIndustryData = useSelector(
    (state: RootState) => state.CTSectorIndustryListSlice.data,
  );
  const deepCompare = (obj1: any, obj2: any) => {
    if (
      typeof obj1 !== "object" ||
      obj1 === null ||
      typeof obj2 !== "object" ||
      obj2 === null
    ) {
      return obj1 === obj2;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (!keys2.includes(key) || !deepCompare(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    dispatch(resetProductState());
    dispatch(fetchPortfolioListData());
    dispatch(fetchSectorIndustryListData());
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: page,
        record: pageSize,
        portfolio: [],
        sectorIndustry: [],
        productProxyOwner: [],
      }),
    );
  }, []);

  const shouldSkipApiCall = (searchText: string): boolean => {
    const hasEmoji = containsEmoji(searchText);
    if (hasEmoji) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!hasUserTyped) {
      return;
    }

    if (shouldSkipApiCall(searchInput)) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      handleSearch();
    }, 350);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchInput, hasUserTyped]);

  useEffect(() => {
    if (portfolioData && portfolioData.length > 0) {
      const checkboxesList: string[] = [];
      portfolioData.forEach((item: { portfolio: string }) => {
        if (item?.portfolio !== "") {
          checkboxesList.push(item.portfolio);
        }
      });

      const sectorIndustryList: string[] = [];
      sectorIndustryData?.forEach((item: { Name: string }) => {
        if (item?.Name !== "") {
          sectorIndustryList.push(item.Name);
        }
      });

      setAccordions([
        {
          title: "CT Portfolio",
          checkboxes: checkboxesList,
        },
        {
          title: "Sector/Industry",
          checkboxes: sectorIndustryList,
        },
      ]);
    }
  }, [portfolioData, sectorIndustryData]);

  useEffect(() => {
    if (accordions.length > 0) {
      const newState = accordions.map((accord, index) => {
        const checkboxes = accord.checkboxes.map((chk, indexChk) => ({
          name: chk,
          checked: false,
          id: indexChk,
        }));

        return { accordion: accord.title, id: index, checkboxes };
      });
      setAccordionDet(newState);
      setAccordionsDetApplied(newState);
    }
  }, [accordions]);

  useEffect(() => {
    const sumAcc = accordionsDet.reduce((sum, p) => {
      const sumCheck = p.checkboxes.reduce(
        (sum2, c) => (c.checked === true ? sum2 + 1 : sum2),
        0,
      );

      return sum + sumCheck;
    }, 0);
    setBadge(sumAcc);
  }, [accordionsDet]);

  useEffect(() => {
    const notResultArray = accordionsDet.map((accDetail) =>
      accDetail.checkboxes.some((chk) =>
        chk.name.toLowerCase().includes(searchFilter.toLowerCase()),
      ),
    );
    setNoResult(
      accordionsDet.length && notResultArray.every((result) => result !== true),
    );
  }, [searchFilter, accordionsDet]);

  useEffect(() => {
    const hasSearchFilter = searchFilter.trim().length > 0;
    setOpenAccordionState(hasSearchFilter);
    setOpenAccordionClosed([]);
  }, [searchFilter]);

  const isLoading = useSelector(
    (state: RootState) => state.CTProductListSlice.isLoading,
  );
  const data = useSelector((state: RootState) => state.CTProductListSlice.data);
  const error = useSelector(
    (state: RootState) => state.CTProductListSlice.error,
  );

  const userPermissionsData: string[] = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissions,
  );
  const userPermissionsStatus: ApiStatus = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissionsStatus,
  );

  const handleDisplayLink = () => {
    if (userPermissionsStatus === ApiStatus.SUCCESS) {
      if (userPermissionsData && userPermissionsData.length > 0) {
        const skuDatabasePermission = userPermissionsData.filter(
          (permission: string) =>
            permission === "product_commownerproxy" ||
            permission === "product_commowner",
        ).length;
        return skuDatabasePermission > 0 ? true : false;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const handleSearch = () => {
    const portfolioList: string[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "CT Portfolio") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            portfolioList.push(chk.name);
          });
      }
    });
    const sectorIndustryList: string[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "Sector/Industry") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            sectorIndustryList.push(chk.name);
          });
      }
    });
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: 1,
        record: pageSize,
        portfolio: portfolioList,
        sectorIndustry: sectorIndustryList,
        productProxyOwner: [],
      }),
    );
  };

  const handleSearchClear = () => {
    setHasUserTyped(false);
    setSearchInput("");
    const portfolioList: string[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "CT Portfolio") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            portfolioList.push(chk.name);
          });
      }
    });
    const sectorIndustryList: string[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "Sector/Industry") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            sectorIndustryList.push(chk.name);
          });
      }
    });
    dispatch(
      fetchProductListData({
        search: "",
        page: 1,
        record: pageSize,
        portfolio: portfolioList,
        sectorIndustry: sectorIndustryList,
        productProxyOwner: [],
      }),
    );
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleClick = ({
    accordionId,
    checkboxId,
  }: {
    accordionId: number;
    checkboxId: number;
  }) => {
    const accordionsDetCopy = accordionsDet.map((accDetail) => {
      if (accDetail.id === accordionId) {
        const newAccordion = {
          ...accDetail,
          // eslint-disable-next-line no-confusing-arrow
          checkboxes: accDetail.checkboxes.map((checkbox) =>
            checkbox.id === checkboxId
              ? { ...checkbox, checked: !checkbox.checked }
              : checkbox,
          ),
        };

        return newAccordion;
      }

      return accDetail;
    });
    setAccordionDet(accordionsDetCopy);
  };

  const toggleSelectAll = ({ accordionId }: { accordionId: number }) => {
    const newList = selectAll.some((item) => item === accordionId)
      ? selectAll.filter((state) => state !== accordionId)
      : [...selectAll, accordionId];

    // eslint-disable-next-line no-confusing-arrow
    const accordionsDetCopy = accordionsDet.map((acc) =>
      acc.id === accordionId
        ? {
            ...acc,
            checkboxes: acc.checkboxes.map((chk) => ({
              ...chk,
              checked: !selectAll.some((item) => item === accordionId),
            })),
          }
        : acc,
    );
    setSelectAll(newList);
    setAccordionDet(accordionsDetCopy);
  };

  const textItem = (str: string) => {
    const displayStr = str && str.trim() !== "" ? str : "Undefined";
    const parts = displayStr.split(new RegExp(`(${searchFilter})`, "gi"));

    return (
      <span>
        {parts.map((part, ind) => (
          <span
            key={ind}
            style={
              part.toLowerCase() === searchFilter.toLowerCase()
                ? { fontWeight: "bold" }
                : {}
            }
          >
            {part}
          </span>
        ))}
      </span>
    );
  };

  const clearAll = () => {
    const accordionsDetCopy = accordionsDet.map((acc) => ({
      ...acc,
      checkboxes: acc.checkboxes.map((chk) => ({ ...chk, checked: false })),
    }));
    setAccordionDet(accordionsDetCopy);
    setOpenAccordionState(false);
    setOpenAccordionClosed([]);
    setSelectAll([]);
    setSearchFilter("");
    setAccordionsDetApplied(accordionsDetCopy);
    handleFilterClose([], []);
    setIsOpen(false);
  };

  const handleApplyFilters = () => {
    setSearchFilter("");
    const portfolioList: string[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "CT Portfolio") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            portfolioList.push(chk.name);
          });
      }
    });
    const sectorIndustryList: string[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "Sector/Industry") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            sectorIndustryList.push(chk.name);
          });
      }
    });
    handleFilterClose(portfolioList, sectorIndustryList);
    setAccordionsDetApplied(accordionsDet);
    setIsOpen(false);
  };

  const onCloseAccordion = (id: number) => {
    if (!openAccordionState) {
      return;
    }

    setOpenAccordionClosed((previousState) =>
      previousState.includes(id) ? previousState : previousState.concat(id),
    );
  };

  const onOpenAccordion = (id: number) => {
    if (!openAccordionState) {
      return;
    }

    setOpenAccordionClosed((previousState) =>
      previousState.includes(id)
        ? previousState.filter((accordionId) => accordionId !== id)
        : previousState,
    );
  };

  const hasAnyCheckedCheckbox = accordionsDet.some((acc) =>
    acc.checkboxes.some((chk) => chk.checked === true),
  );

  const handleFilterClose = (
    portfolioList: string[],
    sectorIndustryList: string[],
  ) => {
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: 1,
        record: pageSize,
        portfolio: portfolioList,
        sectorIndustry: sectorIndustryList,
        productProxyOwner: [],
      }),
    );
    setIsFilterApplied(hasAnyCheckedCheckbox);
  };

  const handlePageChange = (page: number | string) => {
    setPage(Number(page));
    const portfolioList: string[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "CT Portfolio") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            portfolioList.push(chk.name);
          });
      }
    });
    const sectorIndustryList: string[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "Sector/Industry") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            sectorIndustryList.push(chk.name);
          });
      }
    });
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: Number(page),
        record: pageSize,
        portfolio: portfolioList,
        sectorIndustry: sectorIndustryList,
        productProxyOwner: [],
      }),
    );
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
    const portfolioList: string[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "CT Portfolio") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            portfolioList.push(chk.name);
          });
      }
    });
    const sectorIndustryList: string[] = [];
    accordionsDet.map((acc) => {
      if (acc.accordion == "Sector/Industry") {
        acc.checkboxes
          .filter((chk) => chk.checked === true)
          .map((chk) => {
            sectorIndustryList.push(chk.name);
          });
      }
    });
    dispatch(
      fetchProductListData({
        search: searchInput,
        page: 1,
        record: pageSize,
        portfolio: portfolioList,
        sectorIndustry: sectorIndustryList,
        productProxyOwner: [],
      }),
    );
  };

  const renderNoSearchResult = () => {
    if (isFilterApplied) {
      const header = searchInput
        ? `No result under the filter and by the keyword "${searchInput}"`
        : `No result under the filter`;

      const message = searchInput
        ? "Did you not find any results? We recommend refining your search criteria and/or broadening your filter parameters to enhance your search outcomes."
        : "Did not find any results? We recommend broadening your filter criteria for a more comprehensive search.";

      return (
        <NoSearchResult
          searchValue={searchInput}
          header={header}
          message={message}
        />
      );
    } else {
      return <NoSearchResult searchValue={searchInput} />;
    }
  };

  const handleAiSearchLink = (e: any) => {
    e.stopPropagation();
    window.open(
      "https://aismartsearch.ey.com/home",
      "_blank",
      "noopener,noreferrer",
    );
    return;
  };

  const pageSizeOptions = [12, 24, 36];

  return (
    <div className="marketplace-container-wrapper">
      <section className="commercial-hub-header-banner">
        <div className="commercial-hub-header-content">
          <div className="breadcrumb-grid">
            <BreadcrumbHome
              items={breadcrumbItems}
              ariaLabel="Breadcrumb navigation"
            />
          </div>
          <h1 className="commercial-hub-header-title">Marketplace</h1>
        </div>
      </section>
      <>
        <section className="marketplace-product-section">
          <div
            className="marketplace-product-card-container"
            id="product-card-container"
          >
            <p className="marketplace-product-description">
              Browse and purchase technology products and services from our
              comprehensive catalog.
            </p>
            {
              <>
                <div className="marketplace-product-search-container">
                  <SearchInput
                    id="marketplace-search-input"
                    aria-label="Search"
                    placeholder="Search by Keyword"
                    className="marketplace-product-search"
                    value={searchInput}
                    withOutDebounce={true}
                    onChange={(e) => {
                      setHasUserTyped(true);
                      const isContainEmoji = containsEmoji(e.target.value);
                      if (isContainEmoji) {
                        setSearchValidationError(EMOJI_ERROR_MESSAGE);
                      } else {
                        setSearchValidationError("");
                      }
                      setSearchInput(e.target.value);
                    }}
                    clearSearch={() => {
                      setSearchValidationError("");
                      handleSearchClear();
                    }}
                  ></SearchInput>
                  <div>
                    <MotifButton
                      aria-label="filter"
                      id="dropdown-trigger-1"
                      className="marketplace-product-filter"
                      onClick={toggleDropdown}
                    >
                      <div className="filter-main-section">
                        <span className="filter-main-section-left">
                          <span>Filter</span>
                          {badge ? (
                            <MotifBadge variant={"info"} value={badge} />
                          ) : null}
                        </span>
                        <span className="filter-main-section-right">
                          <MotifIcon
                            size="20"
                            iconFunction={IconoirFilterList}
                          />
                        </span>
                      </div>
                    </MotifButton>
                    {isOpen && (
                      <MotifFilters
                        size="default"
                        className="marketplace-filter-dropdown"
                      >
                        <header>
                          <div>
                            <span>
                              Filter
                              {badge ? (
                                <MotifBadge variant={"info"} value={badge} />
                              ) : null}
                            </span>
                            <span className="filter-button-group">
                              <MotifButton
                                onClick={() => clearAll()}
                                variant="text-alt"
                                disabled={!badge && !searchFilter}
                                className={
                                  badge && searchFilter
                                    ? "clear-filter-button"
                                    : ""
                                }
                              >
                                Clear
                              </MotifButton>
                              <MotifButton
                                onClick={handleApplyFilters}
                                variant="text-alt"
                                disabled={deepCompare(
                                  accordionsDet,
                                  accordionsDetApplied,
                                )}
                                className={
                                  !deepCompare(
                                    accordionsDet,
                                    accordionsDetApplied,
                                  )
                                    ? "clear-filter-button"
                                    : ""
                                }
                              >
                                Apply
                              </MotifButton>
                            </span>
                          </div>
                        </header>
                        <MotifFormField className="dropdown-search-container">
                          <MotifSearch
                            value={searchFilter}
                            aria-label="Search"
                            onChange={(event) => {
                              setSearchFilter(event.target.value);
                            }}
                            onClear={() => {
                              setSearchFilter("");
                            }}
                            placeholder="Search"
                          />
                        </MotifFormField>
                        <div className="accordion-wrapper">
                          {noResult ? (
                            <span>No results were found</span>
                          ) : (
                            accordionsDet.map((accDetail) =>
                              accDetail.checkboxes.some((chk) =>
                                chk.name
                                  .toLowerCase()
                                  .includes(searchFilter.toLowerCase()),
                              ) ? (
                                <MotifAccordion
                                  key={accDetail.id}
                                  open={
                                    openAccordionState
                                      ? !openAccordionClosed.includes(
                                          accDetail.id,
                                        )
                                      : undefined
                                  }
                                  onOpen={() => {
                                    onOpenAccordion(accDetail.id);
                                  }}
                                  onClose={() => {
                                    onCloseAccordion(accDetail.id);
                                  }}
                                  alignIconRight={true}
                                  useChevronIcon={true}
                                  className="accordion-list"
                                >
                                  <MotifAccordionTrigger>
                                    {accDetail.accordion}
                                  </MotifAccordionTrigger>
                                  <MotifAccordionContent>
                                    {!searchFilter && (
                                      <>
                                        <MotifFormField className="motif-select-all-options">
                                          <MotifCheckbox
                                            id={`checkbox-selectAll-${accDetail.id}`}
                                            value="checkbox-example"
                                            name="checkbox-example"
                                            indeterminate={
                                              selectAll.some(
                                                (item) => item === accDetail.id,
                                              ) &&
                                              accDetail.checkboxes.some(
                                                (item) =>
                                                  item.checked === false,
                                              )
                                            }
                                            checked={selectAll.some(
                                              (item) => item === accDetail.id,
                                            )}
                                            onChange={() => {
                                              toggleSelectAll({
                                                accordionId: accDetail.id,
                                              });
                                            }}
                                          >
                                            {selectAll.some(
                                              (item) => item === accDetail.id,
                                            )
                                              ? "Deselect all"
                                              : "Select all"}
                                          </MotifCheckbox>
                                        </MotifFormField>
                                      </>
                                    )}
                                    {accDetail.checkboxes.map((chk) =>
                                      chk.name
                                        .toLowerCase()
                                        .includes(
                                          searchFilter.toLowerCase(),
                                        ) ? (
                                        <MotifFormField key={chk.id}>
                                          <MotifCheckbox
                                            id={`checkbox-${accDetail.accordion}-${chk.id}`}
                                            value="checkbox-example"
                                            name="checkbox-example"
                                            checked={chk.checked}
                                            onChange={() => {
                                              handleClick({
                                                accordionId: accDetail.id,
                                                checkboxId: chk.id,
                                              });
                                            }}
                                          >
                                            {textItem(chk.name)}
                                          </MotifCheckbox>
                                        </MotifFormField>
                                      ) : null,
                                    )}
                                  </MotifAccordionContent>
                                </MotifAccordion>
                              ) : null,
                            )
                          )}
                        </div>
                      </MotifFilters>
                    )}
                  </div>
                  {searchValidationError && (
                    <MotifErrorMessage className="search-helper-text">
                      {searchValidationError}
                    </MotifErrorMessage>
                  )}
                </div>
                {isLoading && (
                  <MotifProgressLoader
                    aria-busy="true"
                    aria-label="This page is loading"
                    aria-live="polite"
                    arialabel="This page is loading"
                    show
                    variant="default"
                    className="marketplace-progress"
                  />
                )}
                {!isLoading && !data && error && (
                  <div className="marketplace-empty-productlist">
                    <ErrorOrderPage
                      image={productListEmptyImg}
                      header="An error has occurred while attempting to retrieve data. Please choose the option to refresh the page."
                      handleRefresh={() => {
                        dispatch(resetProductState());
                        dispatch(fetchPortfolioListData());
                        dispatch(
                          fetchProductListData({
                            search: searchInput,
                            page: page,
                            record: pageSize,
                            portfolio: [],
                            sectorIndustry: [],
                            productProxyOwner: [],
                          }),
                        );
                      }}
                    />
                  </div>
                )}
                {!isLoading && data && !error && (
                  <>
                    {data.Data && data.Data.length > 0 ? (
                      <>
                        <div className="marketplace-productlist">
                          {data.Data.map((product: any) => (
                            <ProductCard
                              key={product.ProductId}
                              ProductId={product.ProductId}
                              Name={product.Name}
                              Description={product.Description}
                            />
                          ))}
                          {/* AI Smart Search Tile */}
                          {data.Page === data.TotalPages && (
                            <ProductCard
                              className="ai-smart-search-product-card"
                              key="ai-smart-search"
                              ProductId="ai-smart-search"
                              ButtonName="Try Now"
                              Name={AI_SMART_SEARCH_LINK_TITLE}
                              Description={
                                <>
                                  {AI_SMART_SEARCH_LINK_PRODUCT_CARD_DESC}{" "}
                                  <span
                                    className="ai-smart-search-link"
                                    onClick={handleAiSearchLink}
                                  >
                                    {AI_SMART_SEARCH}.
                                  </span>
                                </>
                              }
                              onClick={handleAiSearchLink}
                            />
                          )}
                        </div>
                        <div className="marketplace-pagination">
                          <div className="marketplace-pagination-text">
                            Show
                          </div>
                          <MotifFormField className="marketplace-select-pageSize">
                            <MotifSelect
                              ariaLabelledBy="select-label"
                              onChange={(pageSize) =>
                                handlePageSizeChange(Number(pageSize))
                              }
                              value={String(pageSize)}
                              visibleOptions={3}
                            >
                              {pageSizeOptions.map((size) => (
                                <MotifOption key={size} value={String(size)}>
                                  {size}
                                </MotifOption>
                              ))}
                            </MotifSelect>
                          </MotifFormField>
                          <MotifPagination
                            allowInput={false}
                            aria-label="Pagination Navigation"
                            currentPage={data.Page}
                            firstButtonProps={{
                              title: "Go to first page",
                            }}
                            lastButtonProps={{
                              title: "Go to last page",
                            }}
                            max={data.TotalPages}
                            min={1}
                            nextButtonProps={{
                              title: "Go to next page",
                            }}
                            onPageChange={(page: number | string) =>
                              handlePageChange(page)
                            }
                            prevButtonProps={{
                              title: "Go to previous page",
                            }}
                            role="navigation"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        {searchInput == "" &&
                        !isFilterApplied &&
                        !hasUserTyped ? (
                          <div className="marketplace-empty-productlist-default">
                            <div>
                              <img
                                src={productListEmptyImg}
                                alt="Empty Product List"
                              />
                            </div>
                            <div className="empty-productlist-header">
                              There are currently no technology products
                              available for purchase
                            </div>
                            {handleDisplayLink() == true && (
                              <>
                                <div className="empty-productlist-sub-header">
                                  Create your first one to start your products.
                                  Once you do, they'll appear here.
                                </div>
                                <div className="empty-productlist-button-wrapper">
                                  <MotifButton
                                    className="empty-productlist-button"
                                    onClick={() => navigate("/ct/")}
                                  >
                                    Create new product +
                                  </MotifButton>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          renderNoSearchResult()
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            }
          </div>
        </section>
        {/* AI Smart Search Banner */}
        {(!data ||
          !data.Data ||
          data.Data.length === 0 ||
          (data && data.Data && data.Data.length > 0)) && (
          <div className="ai-smart-search-banner">
            <div className="ai-smart-search-link-banner-title1">
              {AI_SMART_SEARCH_LINK_TITLE}{" "}
              <span className="ai-smart-search-link-banner-title2">
                {AI_SMART_SEARCH_LINK_BANNER_DESC1}
              </span>
            </div>

            <div className="ai-smart-search-link-banner-description">
              {AI_SMART_SEARCH_LINK_BANNER_DESC}{" "}
              <span
                className="ai-smart-search-link"
                onClick={handleAiSearchLink}
              >
                {AI_SMART_SEARCH}
              </span>
              .
            </div>
          </div>
        )}
      </>
    </div>
  );
};

export default CTMarketplace;
